import { useState } from 'react';

// Self-hosted forms gateway (matou_forms role in matou-distributed-servers):
// validates + rate-limits, emails contact@matou.nz, and sends the joiner an
// acknowledgement via Stalwart.
const JOIN_ENDPOINT = 'https://forms.matou.nz/api/join/matou';

const INVOLVEMENT = [
  'Follow along with the kaupapa',
  'Participate in discussions and design calls',
  'Actively contribute and work on things',
];
const INVOLVEMENT_FOLLOW = INVOLVEMENT[0];
const INVOLVEMENT_DISCUSSIONS = INVOLVEMENT[1];
const INVOLVEMENT_CONTRIBUTE = INVOLVEMENT[2];

const AREAS = ['Engineering', 'Design / Communications', 'Operations', 'Research'];
const AREA_ENGINEERING = AREAS[0];
const AREA_DESIGN = AREAS[1];
const AREA_OPERATIONS = AREAS[2];
const AREA_RESEARCH = AREAS[3];
const DESIGN_FOCUS = ['Video', 'Social', 'Writing', 'Visual'];
const UPDATE_KINDS = [
  'Opportunities to engage',
  'Research / insights',
  'Community stories',
  'Upcoming events / hui',
  'Project updates',
];
const CHANNELS = ['Blog', 'Hui updates / meeting notes', 'Discord', 'Email'];
const COLLECTIVE_EXP = ['No', 'Somewhat', 'Yes'];

const linkCls = 'text-matou-teal-medium hover:underline';

// Mirrors the onboarding ack email built in the gateway's join-handler.mjs
// — keep the two in sync.
const NEXT_STEPS: [string, React.ReactNode][] = [
  ['Register as a contributor', "That's this form — you're officially registering your interest."],
  ['Whakawhānaunga session', <>A short kōrero where we introduce ourselves and welcome you into the whānau. Sessions run every Tuesday and Thursday (NZT) at 7am, 12pm, and 7pm — <a href="/whakawhanaunga" className={linkCls}>book your session</a>.</>],
  ['Work through the onboarding documentation', <>Go at your own pace at <a href="https://docs.matou.nz" className={linkCls}>docs.matou.nz</a>. Our team will reach out to arrange a short onboarding call to support you and answer any pātai.</>],
  ['Begin contributing', 'Choose an area that fits your skills and interests, and begin your mahi with the whānau.'],
];

const inputCls =
  'w-full px-4 py-3 border border-black/10 rounded-md font-mono text-sm text-matou-text bg-matou-bg focus:outline-none focus:ring-2 focus:ring-matou-teal-medium focus:border-matou-teal-medium transition-colors';
const labelCls =
  'block font-mono text-xs font-semibold text-matou-text uppercase tracking-wider mb-2';

function Field({ label, value, onChange, type = 'text', required = false, placeholder = '' }: {
  label: string; value: string; onChange: (v: string) => void;
  type?: string; required?: boolean; placeholder?: string;
}) {
  const id = label.replace(/\W+/g, '-').toLowerCase();
  return (
    <div>
      <label htmlFor={id} className={labelCls}>{label}{required ? ' *' : ''}</label>
      <input id={id} type={type} value={value} required={required} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)} className={inputCls} />
    </div>
  );
}

function AreaField({ label, value, onChange, caption = '', placeholder = '' }: {
  label: string; value: string; onChange: (v: string) => void;
  caption?: string; placeholder?: string;
}) {
  const id = label.replace(/\W+/g, '-').toLowerCase();
  return (
    <div>
      <label htmlFor={id} className={labelCls}>{label}</label>
      {caption && <p className="font-mono text-xs text-matou-text-muted mb-2">{caption}</p>}
      <textarea id={id} value={value} rows={3} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)} className={`${inputCls} resize-none`} />
    </div>
  );
}

function CheckGroup({ label, options, selected, onToggle, single = false, name }: {
  label: string; options: string[]; selected: string[];
  onToggle: (v: string) => void; single?: boolean; name?: string;
}) {
  return (
    <fieldset>
      <legend className={labelCls}>{label}</legend>
      <div className="space-y-2">
        {options.map((opt) => (
          <label key={opt} className="flex items-start gap-3 font-mono text-sm cursor-pointer">
            <input
              type={single ? 'radio' : 'checkbox'}
              checked={selected.includes(opt)}
              onChange={() => onToggle(opt)}
              className="mt-0.5 accent-matou-teal-medium"
              {...(single && name ? { name } : {})}
            />
            <span>{opt}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-matou-surface rounded-lg border border-black/10 p-6 md:p-8 space-y-5">
      <h2 className="font-heading text-lg font-bold text-matou-text">{title}</h2>
      {children}
    </div>
  );
}

const initial = {
  name: '', email: '', location: '',
  indigenousConnection: '',
  involvement: [] as string[],
  contributeAreas: [] as string[],
  engExperience: '', engAreaOfTech: '', engLinks: '', engExcitedAbout: '',
  designFocus: [] as string[], designPortfolio: '',
  operationsAreas: '',
  researchExpertise: '', researchProjects: '',
  discussionThemes: '', collectiveExperience: '', timezones: '',
  followUpdates: [] as string[], followChannels: [] as string[], encouragement: '',
  why: '',
  website: '', // honeypot — hidden field real users never fill
};

export default function JoinForm() {
  const [f, setF] = useState(initial);
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const set = <K extends keyof typeof initial>(k: K) => (v: (typeof initial)[K]) =>
    setF((p) => ({ ...p, [k]: v }));
  const toggle = (k: 'involvement' | 'contributeAreas' | 'designFocus' | 'followUpdates' | 'followChannels') =>
    (v: string) =>
      setF((p) => ({
        ...p,
        [k]: p[k].includes(v) ? p[k].filter((x) => x !== v) : [...p[k], v],
      }));

  const has = (v: string) => f.involvement.includes(v);
  const hasArea = (v: string) => f.contributeAreas.includes(v);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');
    try {
      const payload = {
        name: f.name,
        email: f.email,
        location: f.location,
        indigenousConnection: f.indigenousConnection,
        involvement: f.involvement,
        contributeAreas: has(INVOLVEMENT_CONTRIBUTE) ? f.contributeAreas : [],
        engineering: has(INVOLVEMENT_CONTRIBUTE) && hasArea(AREA_ENGINEERING) ? { experience: f.engExperience, areaOfTech: f.engAreaOfTech, links: f.engLinks, excitedAbout: f.engExcitedAbout } : undefined,
        design: has(INVOLVEMENT_CONTRIBUTE) && hasArea(AREA_DESIGN) ? { focus: f.designFocus, portfolio: f.designPortfolio } : undefined,
        operations: has(INVOLVEMENT_CONTRIBUTE) && hasArea(AREA_OPERATIONS) ? { areas: f.operationsAreas } : undefined,
        research: has(INVOLVEMENT_CONTRIBUTE) && hasArea(AREA_RESEARCH) ? { expertise: f.researchExpertise, projects: f.researchProjects } : undefined,
        discussions: has(INVOLVEMENT_DISCUSSIONS) ? { themes: f.discussionThemes, collectiveExperience: f.collectiveExperience, timezones: f.timezones } : undefined,
        followAlong: has(INVOLVEMENT_FOLLOW) ? { updates: f.followUpdates, channels: f.followChannels, encouragement: f.encouragement } : undefined,
        why: f.why,
        website: f.website,
      };
      const response = await fetch(JOIN_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      setStatus(response.ok ? 'success' : 'error');
    } catch {
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div className="bg-matou-surface rounded-lg border border-black/10 p-6 md:p-8 space-y-4">
        <h2 className="font-heading text-xl font-bold text-matou-text">
          Ngā mihi — your registration is in
        </h2>
        <p className="font-mono text-sm leading-relaxed">
          Thank you for registering your interest in joining Mātou Collective.
          Look out for a confirmation email with what happens next. First up:{' '}
          <a href="/whakawhanaunga" className={linkCls}>book your whakawhānaunga session</a>{' '}
          (Tuesdays and Thursdays NZT — 7am, 12pm, and 7pm), then work through
          the <a href="https://docs.matou.nz" className={linkCls}>onboarding documentation</a>{' '}
          at your own pace.
        </p>
        <p className="font-mono text-sm leading-relaxed">
          Any pātai in the meantime:{' '}
          <a href="mailto:contact@matou.nz" className="text-matou-teal-medium hover:underline">contact@matou.nz</a>
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* Honeypot: hidden from real users; the gateway silently drops any
          submission where this field is filled in */}
      <div className="absolute -left-[9999px]" aria-hidden="true">
        <label htmlFor="join-website">Website</label>
        <input type="text" id="join-website" name="website" value={f.website} tabIndex={-1}
          autoComplete="off" onChange={(e) => set('website')(e.target.value)} />
      </div>

      <Section title="Your details">
        <Field label="Ko wai koe? / Full Name" value={f.name} onChange={set('name')} required />
        <Field label="He aha tō īmēra? / Email" value={f.email} onChange={set('email')} type="email" required />
        <Field label="Nō hea koe? / Where are you from?" value={f.location} onChange={set('location')} />
      </Section>

      <Section title="Indigenous connection">
        <AreaField
          label="What Indigenous or First Nations do you connect with?"
          caption="This could be through whakapapa (blood relations), the place you call home, or any other connection that feels meaningful to you."
          value={f.indigenousConnection}
          onChange={set('indigenousConnection')}
        />
      </Section>

      <Section title="Pūkengatanga">
        <CheckGroup
          label="He aha tōu pūkenga? / How would you like to be involved"
          options={INVOLVEMENT}
          selected={f.involvement}
          onToggle={toggle('involvement')}
        />
      </Section>

      {has(INVOLVEMENT_CONTRIBUTE) && (
        <Section title="Actively contribute and work on things">
          <CheckGroup
            label="Which areas are you most interested in contributing to? (select all that apply)"
            options={AREAS}
            selected={f.contributeAreas}
            onToggle={toggle('contributeAreas')}
          />
          {hasArea(AREA_ENGINEERING) && (
            <div className="space-y-5 border-l-2 border-matou-teal-soft pl-4">
              <AreaField label="How long have you worked as an engineer?" placeholder="e.g., <1 year, 1–3 years, 3–5 years, 5+ years" value={f.engExperience} onChange={set('engExperience')} />
              <AreaField label="Which area of tech do you primarily work in?" value={f.engAreaOfTech} onChange={set('engAreaOfTech')} />
              <Field label="Portfolio, GitHub, or project links (if applicable)" type="url" value={f.engLinks} onChange={set('engLinks')} />
              <AreaField label="What kinds of projects or problems are you most excited to work on?" value={f.engExcitedAbout} onChange={set('engExcitedAbout')} />
            </div>
          )}
          {hasArea(AREA_DESIGN) && (
            <div className="space-y-5 border-l-2 border-matou-teal-soft pl-4">
              <CheckGroup label="Which type of work do you focus on? (select all that apply)" options={DESIGN_FOCUS} selected={f.designFocus} onToggle={toggle('designFocus')} />
              <Field label="Portfolio link" type="url" value={f.designPortfolio} onChange={set('designPortfolio')} />
            </div>
          )}
          {hasArea(AREA_OPERATIONS) && (
            <div className="space-y-5 border-l-2 border-matou-teal-soft pl-4">
              <AreaField label="Which areas of operations do you enjoy most?" value={f.operationsAreas} onChange={set('operationsAreas')} />
            </div>
          )}
          {hasArea(AREA_RESEARCH) && (
            <div className="space-y-5 border-l-2 border-matou-teal-soft pl-4">
              <AreaField label="Area of expertise or interest" value={f.researchExpertise} onChange={set('researchExpertise')} />
              <AreaField label="Specific projects or types of work you'd like to contribute to" value={f.researchProjects} onChange={set('researchProjects')} />
            </div>
          )}
        </Section>
      )}

      {has(INVOLVEMENT_DISCUSSIONS) && (
        <Section title="Discussions and design calls">
          <AreaField label="What kinds of kaupapa or themes are you most drawn to discussing or shaping?" value={f.discussionThemes} onChange={set('discussionThemes')} />
          <CheckGroup
            label="Do you have experience being part of collectives or co-design spaces before?"
            options={COLLECTIVE_EXP}
            selected={f.collectiveExperience ? [f.collectiveExperience] : []}
            onToggle={(v) => set('collectiveExperience')(f.collectiveExperience === v ? '' : v)}
            single
            name="collectiveExperience"
          />
          <Field label="What time zones or days generally work best for you to join calls?" value={f.timezones} onChange={set('timezones')} />
        </Section>
      )}

      {has(INVOLVEMENT_FOLLOW) && (
        <Section title="Follow along with the kaupapa">
          <CheckGroup label="What kinds of updates or kōrero are you most interested in? (select all that apply)" options={UPDATE_KINDS} selected={f.followUpdates} onToggle={toggle('followUpdates')} />
          <CheckGroup label="How would you like to stay connected? (select all that apply)" options={CHANNELS} selected={f.followChannels} onToggle={toggle('followChannels')} />
          <Field label="Is there anything that might encourage you to get more involved over time? (optional)" value={f.encouragement} onChange={set('encouragement')} />
        </Section>
      )}

      <Section title="Your why">
        <AreaField label="He aha koe i hiahia ai ki te hono atu? / Why would you like to join?" value={f.why} onChange={set('why')} />
      </Section>

      <Section title="What happens next">
        <ol className="space-y-4">
          {NEXT_STEPS.map(([title, detail], i) => (
            <li key={title} className="flex gap-4">
              <span className="font-mono text-sm font-bold text-matou-teal-medium flex-shrink-0">{i + 1}.</span>
              <div>
                <p className="font-mono text-sm font-semibold text-matou-text">{title}</p>
                <p className="font-mono text-xs text-matou-text-muted leading-relaxed">{detail}</p>
              </div>
            </li>
          ))}
        </ol>
      </Section>

      <div className="space-y-5">
        <p className="font-mono text-xs text-matou-text-muted leading-relaxed">
          By submitting this form, you consent to Mātou Collective collecting
          and using the information you provide to contact you about your
          registration, participation, and future contributions to Mātou
          Collective and related initiatives. Your information will be used
          only for purposes connected to Mātou Collective's community,
          research, and development activities. We will never share your
          personal details with third parties without your permission. You can
          access, update, or request deletion of your information at any time
          by contacting{' '}
          <a href="mailto:contact@matou.nz" className="text-matou-teal-medium hover:underline">contact@matou.nz</a>.
        </p>

        {status === 'error' && (
          <div className="bg-accessibility-fill border border-accessibility-stroke/30 rounded-md p-4" role="alert">
            <p className="font-mono text-sm text-accessibility-stroke">
              Sorry, something went wrong. Please try again, or email us
              directly at contact@matou.nz.
            </p>
          </div>
        )}

        <button
          type="submit"
          disabled={status === 'submitting'}
          className="w-full font-mono text-sm font-semibold bg-matou-teal-medium text-white px-6 py-3 rounded-md hover:bg-matou-teal-deep disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-150"
        >
          {status === 'submitting' ? 'Sending…' : 'Register your interest'}
        </button>
      </div>
    </form>
  );
}
